import { ApolloClient } from '@apollo/client';
import pRetry from 'p-retry';
import { fromPromise } from 'xstate';

import { TsRestClient } from '@eluve/api-client-provider';
import { ScheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { isValidTimestamp } from '@eluve/date-utils';
import {
  ApiError,
  AppointmentNotAccessibleError,
  HttpError,
  NetworkError,
  ValidationError,
  formatErrorForLogging,
} from '@eluve/eluve-errors';
import { createDefaultLogActionFromCorrelatedLogger } from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';
import { getAppointmentTotalDuration } from '@eluve/shared-gql-operations';
import { calculateFileMd5 } from '@eluve/utils';

import { safeGetAudioDurationFromFile } from './getAudioDurationFromFile';
import {
  CheckFileOutput,
  presignedUploadMachine,
} from './presignedUrlUpload.machine';
import { UploadFileArgs } from './uploadFile.machine';

export const defaultPresignedUploadMachine = ({
  apiClient,
  log,
  apolloClient,
  clientOrigin,
  deviceId,
  scheduleBackgroundTask,
}: {
  apiClient: TsRestClient;
  apolloClient: ApolloClient<unknown>;
  log: CorrelatedLogger;
  clientOrigin?: string;
  deviceId: string;
  scheduleBackgroundTask: ScheduleBackgroundTaskAction;
}) => {
  return presignedUploadMachine.provide({
    actors: {
      checkFile: fromPromise<CheckFileOutput, UploadFileArgs>(
        async ({ input }) => {
          const { file, recordingStartedAt } = input;

          if (!isValidTimestamp(recordingStartedAt)) {
            throw new ValidationError('Invalid recording timestamp format');
          }

          const [recordingDuration, fileHash] = await Promise.all([
            safeGetAudioDurationFromFile({ file, log }),
            calculateFileMd5(file, log),
          ]);

          return {
            recordingDuration,
            fileHash,
          };
        },
      ),
      generatePresignedUrl: fromPromise(async ({ input }) => {
        const {
          tenantId,
          appointmentId,
          file,
          uploadType,
          checkFileOutput: { fileHash },
          format,
          localUploadId,
          networkRetryConfig: { retries, retryDelayMs },
        } = input;

        let fileUploadUrl = '';
        let isUploadRequired = true;
        let bytesAlreadyUploaded: number | undefined;

        let fileUploadId = localUploadId;

        await pRetry(
          async () => {
            let generateSignedUrlResponse;
            try {
              generateSignedUrlResponse =
                await apiClient.appointments.prepareAppointmentFileUpload({
                  params: {
                    tenantId,
                    appointmentId,
                  },
                  body: {
                    fileSize: file.size,
                    fileHash,
                    extension: format.extension,
                    fileType: uploadType,
                    uploadId: localUploadId,
                    fileName: file.name,
                    origin: clientOrigin,
                    deviceId,
                  },
                });
            } catch (err) {
              throw new NetworkError(
                `prepareAppointmentFileUpload fetch failed: ${formatErrorForLogging(err)}`,
                {
                  error: err instanceof Error ? err : undefined,
                  metadata: {
                    operation: 'appointments.prepareAppointmentFileUpload',
                    method: 'POST',
                    tenantId,
                    appointmentId,
                  },
                },
              );
            }

            if (
              generateSignedUrlResponse.status === 403 ||
              generateSignedUrlResponse.status === 404
            ) {
              // The (tenantId, appointmentId) pair does not exist for this
              // caller — the local file row will never succeed. Throw a
              // terminal error so the upload machine tears down IDB + OPFS
              // instead of retrying forever.
              throw new AppointmentNotAccessibleError(
                `prepareAppointmentFileUpload returned ${generateSignedUrlResponse.status}`,
              );
            }

            if (![200, 201].includes(generateSignedUrlResponse.status)) {
              throw new ApiError('Failed to generate signed url');
            }

            if (generateSignedUrlResponse.status === 201) {
              const { uploadURL, uploadId, bytesUploaded } =
                generateSignedUrlResponse.body;
              fileUploadUrl = uploadURL;
              fileUploadId = uploadId;
              bytesAlreadyUploaded = bytesUploaded;
            }
            if (
              generateSignedUrlResponse.status === 200 &&
              generateSignedUrlResponse.body.status === 'FILE_ALREADY_EXISTS'
            ) {
              const { uploadId } = generateSignedUrlResponse.body;
              isUploadRequired = false;
              fileUploadId = uploadId;
            }
          },
          {
            // Allow retry up to 3 times with delay to address intermittent client network issues
            retries,
            shouldRetry: (error) => {
              if (error instanceof AppointmentNotAccessibleError) {
                return false;
              }
              return navigator.onLine;
            },
            onFailedAttempt: () =>
              new Promise((resolve) => setTimeout(resolve, retryDelayMs)),
          },
        );

        return {
          preSignedUrl: fileUploadUrl,
          isUploadRequired,
          fileUploadId,
          bytesUploaded: bytesAlreadyUploaded,
        };
      }),

      uploadFile: fromPromise(async ({ input }) => {
        const {
          actorRef,
          file,
          fileUploadUrl,
          mimeType,
          bytesUploaded = 0,
          networkRetryConfig: { retries, retryDelayMs },
        } = input;

        await pRetry(
          async () => {
            await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              // Stall watchdog: a dead socket (captive portal, black-hole
              // proxy) never fires onload/onerror, parking the sync machine in
              // UploadingFile for the life of the tab. Progress-based so a
              // large-but-alive upload on a slow link is never killed — only
              // zero progress for the full window aborts, which rejects and
              // lets pRetry issue a fresh PUT.
              const STALL_TIMEOUT_MS = 60_000;
              let stallTimer: ReturnType<typeof setTimeout> | undefined;
              let stalled = false;
              const resetStallTimer = () => {
                clearTimeout(stallTimer);
                stallTimer = setTimeout(() => {
                  stalled = true;
                  xhr.abort();
                }, STALL_TIMEOUT_MS);
              };

              // Slice file from resume point (upload entire remaining portion)
              const fileToUpload =
                bytesUploaded > 0 ? file.slice(bytesUploaded) : file;
              const startByte = bytesUploaded;
              const endByte = file.size - 1;
              const totalSize = file.size;

              actorRef?.send({
                type: 'UPLOAD_FILE.UPLOADING',
                progress: Math.round((startByte / totalSize) * 100),
              });

              xhr.open('PUT', fileUploadUrl, true);
              xhr.setRequestHeader('Content-Type', mimeType);

              // Add Content-Range header if resuming
              if (bytesUploaded > 0 && bytesUploaded < file.size) {
                xhr.setRequestHeader(
                  'Content-Range',
                  `bytes ${startByte}-${endByte}/${totalSize}`,
                );
              }

              xhr.upload.addEventListener('progress', (event) => {
                resetStallTimer();
                if (event.lengthComputable) {
                  // Add already-uploaded bytes to progress
                  const totalProgress = startByte + event.loaded;
                  const progress = Math.round(
                    (totalProgress / totalSize) * 100,
                  );
                  actorRef?.send({
                    type: 'UPLOAD_FILE.UPLOADING',
                    progress,
                  });
                }
              });

              xhr.onabort = () => {
                clearTimeout(stallTimer);
                if (stalled) {
                  reject(
                    new NetworkError(
                      `File upload stalled — no progress for ${STALL_TIMEOUT_MS}ms`,
                      {
                        metadata: {
                          operation: 'presignedUrlUpload.PUT',
                          method: 'PUT',
                          endpoint: fileUploadUrl,
                        },
                      },
                    ),
                  );
                }
              };

              xhr.onload = () => {
                clearTimeout(stallTimer);
                // Accept 200/201 for resumable uploads
                if (xhr.status === 200 || xhr.status === 201) {
                  resolve(xhr.response);
                } else {
                  reject(
                    new HttpError(
                      `File upload failed with status ${xhr.status}. Response: ${xhr.responseText}`,
                      {
                        metadata: {
                          operation: 'presignedUrlUpload.PUT',
                          method: 'PUT',
                          endpoint: fileUploadUrl,
                          status: xhr.status,
                          statusText: xhr.statusText,
                          responseBody: xhr.responseText,
                        },
                      },
                    ),
                  );
                }
              };

              xhr.onerror = () => {
                clearTimeout(stallTimer);
                reject(
                  new NetworkError(
                    'Failed to upload file due to network error',
                    {
                      metadata: {
                        operation: 'presignedUrlUpload.PUT',
                        method: 'PUT',
                        endpoint: fileUploadUrl,
                      },
                    },
                  ),
                );
              };

              resetStallTimer();
              xhr.send(fileToUpload);
            });
          },
          {
            retries,
            onFailedAttempt: () =>
              new Promise((resolve) => setTimeout(resolve, retryDelayMs)),
          },
        );
      }),
      markFileComplete: fromPromise(async ({ input }) => {
        const {
          appointmentId,
          tenantId,
          fileUploadId,
          shouldBeTranscribed,
          wasDegraded,
          chunkNum,
          recordingDuration,
          recordingStartedAt,
        } = input;
        let result;
        try {
          result = await apiClient.appointments.markFileUploadComplete({
            params: {
              tenantId,
              appointmentId,
            },
            body: {
              uploadId: fileUploadId,
              shouldBeTranscribed,
              wasSegmentDegraded: wasDegraded,
              chunkNum,
              recordingDuration,
              recordingStartedAt,
            },
          });
        } catch (err) {
          throw new NetworkError(
            `markFileUploadComplete fetch failed: ${formatErrorForLogging(err)}`,
            {
              error: err instanceof Error ? err : undefined,
              metadata: {
                operation: 'appointments.markFileUploadComplete',
                method: 'POST',
                tenantId,
                appointmentId,
                fileUploadId,
              },
            },
          );
        }

        if (result.status === 403 || result.status === 404) {
          // Mirror the prepareAppointmentFileUpload terminal path: if access
          // is lost between prepare and mark-complete (e.g. a server-side
          // ACL flip), surface a terminal error so the upload machine routes
          // the local row through CancelOrphan → CleanupFile instead of
          // looping. Defensive — `markFileUploadComplete` does not currently
          // run AppointmentAccessGuard, but its service still throws
          // NotFoundException when neither the owner check nor the
          // isEluveAdmin bypass matches.
          throw new AppointmentNotAccessibleError(
            `markFileUploadComplete returned ${result.status}`,
          );
        }

        if (result.status !== 200) {
          throw new ApiError('Failed to mark file upload as complete');
        }

        void apolloClient.query({
          query: getAppointmentTotalDuration,
          variables: {
            appointmentId,
            tenantId,
          },
        });
      }),
    },
    actions: {
      log: createDefaultLogActionFromCorrelatedLogger(log),
      scheduleBackgroundTask,
    },
  });
};
